// The file that will contain the code to interact with the server
import * as https from 'https'
import environment from './environment.js'

/**
 * Send an HTTP request and execute the corresponding callback
 * when answer is received.
 * 
 * @param options a dict containing HTTP headers 
 * @param callbackSuccess callback in case of success 
 * @param callbackFailure callback in case of failure 
 * @param data what must be sent to the server 
 */
function getRequestResponse(options, callbackSuccess, callbackFailure, data=""){
  
  const req = https.request(options, res => {
    let response = ''

    // When a piece of data is received
    res.on('data', d => {
      response += d;
    })

    // When all of the response has been received
    res.on("end",()=>{
      callbackSuccess(JSON.parse(response));
      })
    })

    // When an error is encountered
    req.on('error', error => {
      callbackFailure(error);
    })

    // No body available for a GET request
    if (options.method != "GET") {
      req.write(data);
    }

    req.end();
}

/**
 * Send a JSON through an HTTP request
 * 
 * @param {*} method HTTP request type ('GET', 'POST', etc)
 * @param {*} body The JSON that must be sent
 * @param {*} callbackSuccess Callback if the request succeeds
 * @param {*} callbackFailure Callback if the request fails
 * @param {*} path The part the url after the domain name (ex : '/home/' in 'www.mywebsite.com/home/')
 * @param {*} url The domain name (ex : 'www.mywebsite.com' in 'www.mywebsite.com/home/')
 * @param {*} port The destination port
 * 
 * @requires noBodyOnGET : method == 'GET' implies body == {}
 */
function sendJSON(method, body, callbackSuccess, callbackFailure, path, url=environment.baseUrl, port = environment.port){
  // Build the headers
  body = JSON.stringify(body);
  const options = {
    hostname : url,
    port : port,
    path : path,
    method : method,
    headers : {
      'Content-Type' : 'application/json',
      'Content-Length' : body.length
    }
  }
  // Send the request
  getRequestResponse(options, callbackSuccess, callbackFailure, body);
}

/**
 * Returns every translation found for a given expression in a given language
 * 
 * @param {*} terms an expression
 * @param {*} lang either 'fr', 'nl' or 'en'
 * @param {*} callbackSuccess what to do in case of success
 * @param {*} callbackFailure what to do in case of failure
 * 
 * @returns (Through the callbackSuccess parameter) A list of every translation found in the database
 *  
 * ex :
 * ```
 * [
 * ...,
 *    [
 *     "myOriginalExpression",
 *     "translationFound",
 *     "language in which a translation has been found"
 *    ],
 * ...
 * ]
 * ```
 */
function getUnivTerminologyTrad(terms, lang, callbackSuccess, callbackFailure){
  sendJSON('POST', {
    'words' : terms,
    'lang' : lang
  },callbackSuccess, callbackFailure, environment.terminoUniv.trad)
}

/**
 * Returns every partial match found for a given expression in a given language
 * 
 * @param {*} terms an expression
 * @param {*} lang either 'fr', 'nl' or 'en'
 * @param {*} callbackSuccess what to do in case of success
 * @param {*} callbackFailure what to do in case of failure
 * 
 * @returns (Through the callbackSuccess parameter) A list of every martialMatch found in the database
 *  
 * ex :
 * ```
 * [
 * ...,
 * "a partial match",
 * "another partial match",
 * ...
 * ]
 * ```
 */
function getUnivTerminologyPartialMatch(terms, lang, callbackSuccess, callbackFailure){
  sendJSON('POST', {
    'words' : terms,
    'lang' : lang
  },callbackSuccess, callbackFailure, environment.terminoUniv.partialMatch)
}

/**
 * Returns every frequent expression found for a given expression in a given language
 * 
 * @param {*} terms an expression
 * @param {*} callbackSuccess what to do in case of success
 * @param {*} callbackFailure what to do in case of failure
 * 
 * @returns (Through the callbackSuccess parameter) A list of every frequentExpression found in the database
 *  
 * ex :
 * ```
 * [
 * ...,
 * "A frequent expression",
 * "Another frequent expression"
 * ...
 * ]
 * ```
 */
function getUnivTerminologyFreqExpr(terms, callbackSuccess, callbackFailure){
  sendJSON('POST', {
    'words' : terms
  },callbackSuccess, callbackFailure, environment.terminoUniv.frequentExpr)
}

/**
 * Returns every suggestions found for a given expression in a given language
 * 
 * @param {*} terms an expression
 * @param {*} lang either 'fr', 'nl' or 'en'
 * @param {*} callbackSuccess what to do in case of success
 * @param {*} callbackFailure what to do in case of failure
 * 
 * @returns (Through the callbackSuccess parameter) A list of every suggestion matching `terms` found in the database
 *  
 * ex : if terms == "univ"
 * ```
 * [
 * "university",
 * "university of California",
 * ...,
 * "state university"
 * ]
 * ```
 */
function getUnivTerminologySuggestions(terms,lang, callbackSuccess, callbackFailure){
  sendJSON('POST', {
    'words' : terms,
    'lang' : lang
  },callbackSuccess, callbackFailure, environment.terminoUniv.suggestions)
}

/**
 * Returns a datastructure indicating which translation has been found for each expression
 * 
 * @param {*} text a text to partially translate
 * @param {*} lang either 'fr', 'nl' or 'en'
 * @param {*} callbackSuccess what to do in case of success
 * @param {*} callbackFailure what to do in case of failure
 * 
 * @returns (Through the callbackSuccess parameter) A list of every words from the original text with it's translation
 *  
 * ex : if terms == "Les programmes de bachelier offrent la formation de base" and lang == "fr"
 * ```
 * [
    [
        "Les",
        ""
    ],
    [
        "programmes de bachelier",
        "BA program"
    ],
    [
        "offrent",
        ""
    ],
    [
        "la",
        ""
    ],
    [
        "formation de base",
        "foundation programme"
    ]
  ]
 * ```
 */
function getTextTranslationHelp(text, lang, callbackSuccess, callbackFailure){
  sendJSON('POST', {
    'lang' : lang,
    'text' : text
  },callbackSuccess, callbackFailure,environment.translationAssistant)
}

/**
 * Returns every suggestions found for a given expression in a given language
 * 
 * @param {*} suggestion a suggestion written by the user
 * @param {*} lang either 'fr', 'nl' or 'en'
 * @param {*} callbackSuccess what to do in case of success
 * @param {*} callbackFailure what to do in case of failure
 */
function submitSuggestion (suggestion, lang, callbackSuccess, callbackFailure){
  sendJSON('POST', {
    'lang' : lang,
    'suggestion' : suggestion 
  },callbackSuccess, callbackFailure,environment.suggestions)
}

/**
 * Returns a random tip from the server
 * 
 * @param {*} callbackSuccess what to do in case of success
 * @param {*} callbackFailure what to do in case of failure
 * 
 * @returns (Through callbackSuccess parameter) A Dictionary representing a tip.
 * 
 * ex :
 * ```javascript
 * {
    answer: "Le pluriel du mot 'analysis' est 'analyses'.<br><em>Het meervoud van het woord 'analysis' is 'analyses'.</em>",
    choices: [
        "analysis",
        "analyses",
        "analyse"
    ],
    comment: "",
    question: "Quel est le pluriel du mot 'analysis'?<br><br><em>Wat is het meervoud van het woord 'analysis'?</em>"
    }
 * ```
 */
function getRandomTip(callbackSuccess, callbackFailure){
  sendJSON('POST', {}, callbackSuccess, callbackFailure, environment.tip)
}

export {
  getUnivTerminologyPartialMatch,
  getUnivTerminologyTrad, 
  getUnivTerminologyFreqExpr, 
  getUnivTerminologySuggestions, 
  getTextTranslationHelp, 
  submitSuggestion, 
  getRandomTip}